// src/views/Tarjetas/NuevaTarjeta/TarjetaCliente.jsx
import React from "react";
import ReactDOM from "react-dom";

import { FaSave, FaArrowLeft } from "react-icons/fa";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";
import PermisoTarjeta from "../../Tarjetas/Configuracion/PermisoTarjeta";
import ClienteAutocomplete from "../../../components/autocomplete/ClienteAutocomplete";
import ProductoMultiSelect from "../../../components/multiselect/ProductoMultiSelect";
import EessMultiSelect from "../../../components/multiselect/EessMultiSelect";
import InputNumberNoScroll from "../../../components/inputs/inputNumberNoScroll";

const formatMiles = (n) => {
  if (n === null || n === undefined || n === "") return "";
  const num = Number(n);
  if (Number.isNaN(num)) return "";
  return num.toLocaleString("es-PY");
};

function TarjetaCliente({
  canSave = true,
  saving = false,
  onClose = () => {},
  onGuardar = () => {},
}) {
  // ===== 1) Cliente — autocomplete + detalle =====
  const [clienteId, setClienteId] = React.useState("");
  const [clienteSeleccionado, setClienteSeleccionado] = React.useState(null);

  const [limiteTotal, setLimiteTotal] = React.useState("");
  const [saldo, setSaldo] = React.useState("");
  const [saldoMax, setSaldoMax] = React.useState(null);
  const [excedioSaldo, setExcedioSaldo] = React.useState(false);

  const abortRef = React.useRef(null);

  // ===== Permiso Tarjeta =====
  const [mostrarPermisos, setMostrarPermisos] = React.useState(false);
  const [tarjetaSel, setTarjetaSel] = React.useState(null);

  // ===== 2) Vehículo =====
  const [marcaId, setMarcaId] = React.useState("");
  const [marcaInput, setMarcaInput] = React.useState("");
  const [openMarcaBox, setOpenMarcaBox] = React.useState(false);

  const [marcaItems, setMarcaItems] = React.useState([]);
  const [marcaPage, setMarcaPage] = React.useState(0);
  const [marcaHasMore, setMarcaHasMore] = React.useState(false);
  const [marcaLoading, setMarcaLoading] = React.useState(false);

  const reqSeqMarcaRef = React.useRef(0);
  const sizeMarcas = 50;

  const [patente, setPatente] = React.useState("");
  const [descripcion, setDescripcion] = React.useState("");
  const [marca, setMarca] = React.useState("");

  // ===== 3) Límite =====
  const [limite, setLimite] = React.useState("");

  // ===== 4) Centro de costo / Observaciones =====
  const [centroCosto, setCentroCosto] = React.useState("");
  const [obs, setObs] = React.useState("");

  // ===== 5) Capacidad tanque =====
  const [capacidadTanque, setCapacidadTanque] = React.useState("");

  // ===== 6) Productos =====
  const [prodSelectedIds, setProdSelectedIds] = React.useState([]);

  // ===== 7) Estaciones =====
  const [estSelectedIds, setEstSelectedIds] = React.useState([]);

  // ===== Validación / Alertas =====
  const [errors, setErrors] = React.useState({});
  const [alerta, setAlerta] = React.useState("");

  // ===== Overlay único =====
  const [overlay, setOverlay] = React.useState({
    open: false,
    mode: "hidden",
    msg: "",
    numero: null,
    clienteId: null,
    valorLimite: null,
  });

  // ===== Helpers =====
  const fmtMoney = (n) => {
    if (n === null || n === undefined || n === "") return "";
    const num = Number(n);
    if (Number.isNaN(num)) return String(n);
    return num.toLocaleString("es-PY");
  };

  const getNumeroTarjeta = (resp) => {
    const r = resp || {};
    return (
      r.numeroTarjeta ??
      r.tarjetaNumero ??
      r.nroTarjeta ??
      r.tarjeta ??
      r.numero ??
      r.idTarjeta ??
      r?.data?.numeroTarjeta ??
      r?.data?.tarjetaNumero ??
      r?.data?.tarjeta ??
      null
    );
  };

  const removeTopSuccessBanner = React.useCallback(() => {
    try {
      const candidates = Array.from(
        document.querySelectorAll(
          ".alert-success, .alert.alert-success, .message-success",
        ),
      );
      candidates.forEach((el) => {
        const txt = (el.textContent || "").toLowerCase();
        if (txt.includes("solicitud creada")) el.remove();
      });
    } catch {}
  }, []);

  const resetForm = () => {
    setClienteSeleccionado(null);
    setClienteId("");
    setLimiteTotal("");
    setSaldo("");
    setSaldoMax(null);
    setExcedioSaldo(false);

    setMarcaId("");
    setMarcaInput("");
    setOpenMarcaBox(false);
    setMarcaItems([]);
    setMarcaPage(0);
    setMarcaHasMore(false);
    setMarcaLoading(false);
    setPatente("");
    setDescripcion("");
    setMarca("");

    setLimite("");
    setCentroCosto("");
    setObs("");
    setCapacidadTanque("");

    setProdSelectedIds([]);
    setEstSelectedIds([]);

    setErrors({});
    setAlerta("");
  };

  // ===== Validación =====
  const validateRequired = () => {
    const newErr = {};

    if (!clienteId) newErr.clienteId = "Debe seleccionar un cliente.";

    if (!patente?.trim()) newErr.patente = "La patente es obligatoria.";

    if (!descripcion?.trim()) {
      newErr.descripcion = "La descripción/modelo es obligatoria.";
    }

    if (!marca) newErr.marca = "Debe seleccionar una marca.";

    if (limite === "" || limite === null) {
      newErr.limite = "El límite es obligatorio.";
    } else {
      const cleanLimit = String(limite).replace(/\./g, "");
      const val = Number(cleanLimit);

      if (Number.isNaN(val)) {
        newErr.limite = "Ingrese un número válido.";
      } else if (val <= 0) {
        newErr.limite = "El límite debe ser mayor a 0.";
      } else if (saldoMax != null && val > saldoMax) {
        newErr.limite = `El límite no puede superar ${fmtMoney(saldoMax)}.`;
      } else if (saldoMax == null && val > 2_000_000) {
        newErr.limite = "El límite no puede superar 2.000.000.";
      }
    }

    if (capacidadTanque !== "" && Number(capacidadTanque) < 0) {
      newErr.capacidadTanque = "La capacidad del tanque no puede ser negativa.";
    }

    if (!prodSelectedIds || prodSelectedIds.length === 0) {
      newErr.productos = "Debe seleccionar al menos un producto.";
    }

    if (!estSelectedIds || estSelectedIds.length === 0) {
      newErr.eess = "Debe seleccionar al menos una estación.";
    }

    setErrors(newErr);
    setAlerta(
      Object.keys(newErr).length
        ? "Debes completar correctamente los campos obligatorios de 1) Cliente, 2) Vehículo, 3) Límite, 6) Productos y 7) Estaciones."
        : "",
    );

    return newErr;
  };

  const touchRequired = () => {
    const miss = {};

    if (!clienteId) miss.clienteId = "Debe seleccionar un cliente.";

    if (!patente?.trim()) miss.patente = "La patente es obligatoria.";

    if (!descripcion?.trim()) {
      miss.descripcion = "La descripción/modelo es obligatoria.";
    }

    if (!marca) miss.marca = "Debe seleccionar una marca.";

    if (limite === "" || limite === null) {
      miss.limite = "El límite es obligatorio.";
    }

    if (!prodSelectedIds || prodSelectedIds.length === 0) {
      miss.productos = "Debe seleccionar al menos un producto.";
    }

    if (!estSelectedIds || estSelectedIds.length === 0) {
      miss.eess = "Debe seleccionar al menos una estación.";
    }

    if (Object.keys(miss).length) {
      setErrors((p) => ({ ...p, ...miss }));
      setAlerta(
        "Debes completar correctamente los campos obligatorios de 1) Cliente, 2) Vehículo, 3) Límite, 6) Productos y 7) Estaciones.",
      );
    }
  };

  // ===== llamadas de red =====
  const fetchClienteDetalle = React.useCallback(async (id) => {
    try {
      const endpoint = apiRoutes.clientes?.detail
        ? apiRoutes.clientes.detail(id)
        : `${apiRoutes.clientes.list.replace(/\/list$/, "")}/${id}`;

      const data = await apiFetchJson(endpoint);

      const creditMax =
        data?.valorCreditomaximo ??
        data?.clieVlCreditomaximo ??
        data?.vlCreditoMaximo ??
        data?.creditoMaximo ??
        data?.creditomaximo ??
        null;

      const saldoRaw = data?.saldo ?? data?.SALDO ?? null;

      setLimiteTotal(creditMax != null ? fmtMoney(creditMax) : "");
      setSaldo(saldoRaw != null ? fmtMoney(saldoRaw) : "");
      setSaldoMax(saldoRaw != null ? Number(saldoRaw) : null);
      setExcedioSaldo(false);
    } catch (e) {
      console.error("Detalle cliente:", e);
      setLimiteTotal("");
      setSaldo("");
    }
  }, []);

  const handleClienteChange = (cliente) => {
    setClienteSeleccionado(cliente);

    if (cliente) {
      setClienteId(String(cliente.id));
      fetchClienteDetalle(cliente.id);
      setErrors((p) => ({ ...p, clienteId: undefined }));
    } else {
      setClienteId("");
      setLimiteTotal("");
      setSaldo("");
      setSaldoMax(null);
      setExcedioSaldo(false);
    }
  };

  // === Marcas: search y browse ===
  const fetchMarcasSearch = React.useCallback(
    async ({ q, page }) => {
      try {
        abortRef.current?.abort();
      } catch {}

      const ctrl = new AbortController();
      abortRef.current = ctrl;

      const seq = ++reqSeqMarcaRef.current;

      if (!page || page === 0) {
        setMarcaItems([]);
        setMarcaHasMore(false);
      }

      setMarcaLoading(true);

      try {
        const endpoint =
          `${apiRoutes.vehiculosConTarjeta.autocomplete}` +
          `?tipo=marca&q=${encodeURIComponent(q ?? "")}` +
          `&page=${encodeURIComponent(page ?? 0)}` +
          `&size=${encodeURIComponent(sizeMarcas)}`;

        const data = await apiFetchJson(endpoint, { signal: ctrl.signal });

        if (seq !== reqSeqMarcaRef.current) return;

        const mapped = (data?.items ?? []).map((m) => ({
          id: m.id,
          nombre: m.marcaNombre ?? "",
        }));

        setMarcaItems((prev) =>
          page && page > 0 ? [...prev, ...mapped] : mapped,
        );

        setMarcaHasMore(Boolean(data?.hasMore));
        setMarcaPage(data?.page ?? page ?? 0);
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("Autocomplete marcas:", e);
          setMarcaItems([]);
          setMarcaHasMore(false);
        }
      } finally {
        setMarcaLoading(false);
      }
    },
    [sizeMarcas],
  );

  const fetchMarcasBrowse = React.useCallback(
    async ({ page }) => {
      try {
        abortRef.current?.abort();
      } catch {}

      const ctrl = new AbortController();
      abortRef.current = ctrl;

      const seq = ++reqSeqMarcaRef.current;

      if (!page || page === 0) {
        setMarcaItems([]);
        setMarcaHasMore(false);
      }

      setMarcaLoading(true);

      try {
        const endpoint =
          `${apiRoutes.vehiculosConTarjeta.autocomplete}` +
          `?tipo=marca&q=&page=${encodeURIComponent(page ?? 0)}` +
          `&size=${encodeURIComponent(sizeMarcas)}`;

        const data = await apiFetchJson(endpoint, { signal: ctrl.signal });

        if (seq !== reqSeqMarcaRef.current) return;

        const mapped = (data?.items ?? []).map((m) => ({
          id: m.id,
          nombre: m.marcaNombre ?? "",
        }));

        setMarcaItems((prev) =>
          page && page > 0 ? [...prev, ...mapped] : mapped,
        );

        setMarcaHasMore(Boolean(data?.hasMore));
        setMarcaPage(data?.page ?? page ?? 0);
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("Browse marcas:", e);
          setMarcaItems([]);
          setMarcaHasMore(false);
        }
      } finally {
        setMarcaLoading(false);
      }
    },
    [sizeMarcas],
  );

  // Cleanup
  React.useEffect(() => {
    return () => {
      try {
        abortRef.current?.abort();
      } catch {}
    };
  }, []);

  React.useEffect(() => {
    if (!mostrarPermisos) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prev;
    };
  }, [mostrarPermisos]);

  // Debounce marca
  React.useEffect(() => {
    if (!openMarcaBox) return;

    const q = (marcaInput ?? "").trim();

    const t = setTimeout(() => {
      if (q) fetchMarcasSearch({ q, page: 0 });
      else fetchMarcasBrowse({ page: 0 });
    }, 250);

    return () => clearTimeout(t);
  }, [marcaInput, openMarcaBox, fetchMarcasSearch, fetchMarcasBrowse]);

  // ===== acciones =====
  const selectMarca = (opt) => {
    const id = opt?.id != null ? String(opt.id) : "";
    const nombre = opt?.nombre ?? "";

    const esMultiproposito =
      id === "12" || nombre.trim().toUpperCase() === "MULTIPROPOSITO";

    setMarcaId(id);
    setMarcaInput(nombre);
    setMarca(nombre);
    setErrors((p) => ({ ...p, marca: undefined }));
    setOpenMarcaBox(false);

    if (esMultiproposito) {
      setPatente("MULTI");
      setDescripcion("MULTI");

      setErrors((p) => ({
        ...p,
        patente: undefined,
        descripcion: undefined,
      }));
    } else {
      setPatente((prev) =>
        prev != null && prev.trim().toUpperCase() === "MULTI" ? "" : prev,
      );

      setDescripcion((prev) =>
        prev != null && prev.trim().toUpperCase() === "MULTI" ? "" : prev,
      );
    }
  };

  // ===== Guardar =====
  const handleGuardar = async () => {
    if (overlay.open && overlay.mode !== "hidden") return;

    const errs = validateRequired();

    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      const el = document.querySelector(`[data-field="${firstKey}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const payload = {
      clienteId: clienteId ? Number(clienteId) : null,
      patente: (patente ?? "").trim(),
      vehiculoDetalles: (descripcion ?? "").trim(),
      vehiculoMarcaId: marcaId ? Number(marcaId) : null,
      capacidadTanque:
        capacidadTanque === "" || capacidadTanque === null
          ? null
          : Number(capacidadTanque),
      limite: Number(String(limite).replace(/\./g, "")),
      centroCosto: (centroCosto ?? "").trim() || null,
      observaciones: (obs ?? "").trim() || null,
      productoIds: prodSelectedIds.map((id) => Number(id)),
      eessIds: estSelectedIds.map((id) => Number(id)),
    };

    try {
      setAlerta("");
      setOverlay({
        open: true,
        mode: "loading",
        msg: "Generando solicitud…",
        numero: null,
      });

      const resp = await apiFetchJson(apiRoutes.tarjetas.solicitarCliente, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      try {
        await onGuardar?.(resp);
      } finally {
        requestAnimationFrame(() => {
          document
            .querySelectorAll(".alert-success, .alert.alert-success")
            .forEach((el) => el.remove());

          Array.from(document.querySelectorAll("div,span")).forEach((el) => {
            const t = el.textContent?.trim() || "";
            if (
              t.startsWith("Solicitud creada.") ||
              t.startsWith("Solicitud creada:")
            ) {
              el.remove();
            }
          });
        });
      }

      const numero = getNumeroTarjeta(resp);
      const idTarjeta = resp?.id ?? resp?.tarjetaId ?? null;
      const clienteNombreResp = resp?.clienteNombre ?? null;

      const vehiculoMarcaResp =
        resp?.vehiculoMarca ?? resp?.vehiculo?.marcaNombre ?? null;

      const vehiculoModeloResp =
        resp?.vehiculoModelo ?? resp?.vehiculo?.modelo ?? null;

      const chapaResp = resp?.chapa ?? resp?.vehiculo?.patente ?? null;

      const okMsg = numero
        ? `Tarjeta creada: ${numero}`
        : "Operación realizada correctamente.";

      const clienteIdSel = clienteId ? Number(clienteId) : null;

      resetForm();

      setOverlay({
        open: true,
        mode: "success",
        msg: okMsg,
        numero,
        id: idTarjeta,
        clienteId: clienteIdSel,
        clienteNombre: clienteNombreResp,
        vehiculoMarca: vehiculoMarcaResp,
        vehiculoModelo: vehiculoModeloResp,
        chapa: chapaResp,
        valorLimite: payload?.limite ?? null,
      });
    } catch (e) {
      console.error("Error al solicitar tarjeta cliente:", e);

      setOverlay({
        open: true,
        mode: "error",
        msg: "No se pudo crear la tarjeta. Verifica los datos e intenta nuevamente.",
        numero: null,
      });
    }
  };

  React.useEffect(() => {
    if (overlay.open && overlay.mode === "success") {
      const t = setTimeout(removeTopSuccessBanner, 30);
      return () => clearTimeout(t);
    }
  }, [overlay.open, overlay.mode, removeTopSuccessBanner]);

  const handleCancelar = () => {
    if (overlay.open && overlay.mode !== "hidden") return;
    onClose();
  };

  const abrirPermisosConTarjetaCreada = React.useCallback(() => {
    if (!overlay?.numero) return;

    setOverlay({ open: false, mode: "hidden", msg: "", numero: null });

    const etiqueta =
      (overlay?.chapa ? overlay.chapa : "") +
      (overlay?.vehiculoMarca || overlay?.vehiculoModelo
        ? ` — ${[overlay?.vehiculoMarca, overlay?.vehiculoModelo]
            .filter(Boolean)
            .join(" ")}`
        : "");

    setTarjetaSel({
      id: overlay?.id ?? null,
      numero: String(overlay.numero),
      clienteId: overlay?.clienteId ?? null,
      codigoTipo: "CLIENTE",
      clienteNombre: overlay?.clienteNombre ?? "—",
      vehiculoMarca: overlay?.vehiculoMarca ?? "—",
      chapa: overlay?.chapa ?? "—",
      vehiculoEtiqueta: etiqueta || "—",
      valorLimite: overlay?.valorLimite ?? null,
    });

    try {
      sessionStorage.setItem(
        "permisoTarjeta.prefill",
        JSON.stringify({
          tarjetaId: overlay?.id ?? null,
          numero: overlay?.numero ?? null,
          clienteNombre: overlay?.clienteNombre ?? null,
          vehiculoMarca: overlay?.vehiculoMarca ?? null,
          vehiculoEtiqueta: etiqueta || null,
          chapa: overlay?.chapa ?? null,
        }),
      );
    } catch {}

    setMostrarPermisos(true);
  }, [
    overlay?.numero,
    overlay?.clienteNombre,
    overlay?.chapa,
    overlay?.vehiculoModelo,
    overlay?.vehiculoMarca,
    overlay?.id,
    overlay?.clienteId,
    overlay?.valorLimite,
  ]);

  const isGuardarEnabled = React.useMemo(() => {
    const bloqueado = overlay.open && overlay.mode !== "hidden";

    const okCliente = !!clienteId;
    const okPatente = !!(patente && patente.trim());
    const okDesc = !!(descripcion && descripcion.trim());
    const okMarca = !!marca;

    if (limite === "" || limite === null) return false;

    const cleanLimit = String(limite).replace(/\./g, "");
    const lim = Number(cleanLimit);

    if (Number.isNaN(lim)) return false;

    const limValido =
      !Number.isNaN(lim) &&
      lim >= 0 &&
      ((saldoMax != null && lim <= saldoMax) ||
        (saldoMax == null && lim <= 2_000_000));

    const capOk = capacidadTanque === "" || Number(capacidadTanque) >= 0;

    const okProductos = prodSelectedIds && prodSelectedIds.length > 0;
    const okEess = estSelectedIds && estSelectedIds.length > 0;

    return (
      canSave &&
      !saving &&
      !bloqueado &&
      okCliente &&
      okPatente &&
      okDesc &&
      okMarca &&
      limValido &&
      capOk &&
      okProductos &&
      okEess
    );
  }, [
    canSave,
    saving,
    overlay,
    clienteId,
    patente,
    descripcion,
    marca,
    limite,
    saldoMax,
    capacidadTanque,
    prodSelectedIds,
    estSelectedIds,
  ]);

  function ModalPortal({ children }) {
    return ReactDOM.createPortal(children, document.body);
  }

  return (
    <div className="page">
      <h2 className="page-title mb-4">
        Introduzca los datos de Solicitar una nueva Tarjeta
      </h2>

      {alerta && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-red-700 text-sm">
          {alerta}
        </div>
      )}

      {/* 1) Cliente */}
      <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2">
        1. Cliente
      </h3>

      <div
        className={`rounded-lg border border-gray-200 bg-gray-50 p-4 md:p-5 mb-4 ${
          overlay.open ? "opacity-70 pointer-events-none select-none" : ""
        }`}
      >
        <div className="space-y-4 text-sm md:text-base">
          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] items-start gap-3">
            <label className="text-gray-700 font-medium">Cliente:</label>

            <div className="w-full" data-field="clienteId">
              <ClienteAutocomplete
                value={clienteSeleccionado}
                onChange={handleClienteChange}
                placeholder="Buscar cliente..."
              />

              {errors.clienteId && (
                <p className="mt-1 text-xs text-red-600">{errors.clienteId}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[220px_240px] items-center gap-3">
            <label className="text-gray-700">Límite total del cliente:</label>

            <input
              className="border border-gray-300 rounded-md px-3 py-2 bg-gray-100"
              value={limiteTotal}
              readOnly
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[220px_240px] items-center gap-3">
            <label className="text-gray-700">Saldo:</label>

            <input
              className="border border-gray-300 rounded-md px-3 py-2 bg-gray-100"
              value={saldo}
              readOnly
            />
          </div>
        </div>
      </div>

      {/* 2) Seleccione Vehículo */}
      <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2">
        2. Seleccione Vehículo
      </h3>

      <div
        className={`rounded-lg border border-gray-200 bg-gray-50 p-4 md:p-5 mb-4 ${
          overlay.open ? "opacity-70 pointer-events-none select-none" : ""
        }`}
      >
        <div className="space-y-4 text-sm md:text-base">
          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] items-center gap-3">
            <label className="text-gray-700">Patente:</label>

            <div data-field="patente">
              <input
                className={`border rounded-md px-3 py-2 bg-white w-full ${
                  errors.patente ? "border-red-500" : "border-gray-300"
                }`}
                style={{ textTransform: "uppercase" }}
                value={patente}
                onFocus={touchRequired}
                onChange={(e) => {
                  setPatente(e.target.value.toUpperCase());
                  setErrors((p) => ({ ...p, patente: undefined }));
                }}
              />

              {errors.patente && (
                <p className="mt-1 text-xs text-red-600">{errors.patente}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[160px_240px] items-center gap-3">
            <label className="text-gray-700">Marca:</label>

            <div data-field="marca" className="relative w-full">
              <input
                type="text"
                placeholder="Escribe para buscar…"
                className={`w-full border rounded-md px-3 py-2 bg-white ${
                  errors.marca ? "border-red-500" : "border-gray-300"
                }`}
                style={{ textTransform: "uppercase" }}
                value={marcaInput}
                onFocus={() => {
                  setErrors((p) => ({ ...p, marca: undefined }));
                  setOpenMarcaBox(true);

                  if (!(marcaInput ?? "").trim()) {
                    fetchMarcasBrowse({ page: 0 });
                  }
                }}
                onChange={(e) => {
                  const v = e.target.value.toUpperCase();

                  setMarcaInput(v);
                  setMarcaId("");
                  setMarca("");
                  setOpenMarcaBox(true);
                  setMarcaItems([]);
                  setMarcaHasMore(false);
                  setMarcaPage(0);

                  setPatente((prev) =>
                    prev != null && prev.trim().toUpperCase() === "MULTI"
                      ? ""
                      : prev,
                  );

                  setDescripcion((prev) =>
                    prev != null && prev.trim().toUpperCase() === "MULTI"
                      ? ""
                      : prev,
                  );
                }}
                onBlur={() => setTimeout(() => setOpenMarcaBox(false), 120)}
              />

              {openMarcaBox && (
                <div
                  className="absolute z-20 mt-1 w-full border rounded bg-white shadow-lg"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <div className="p-2 border-b bg-gray-50 text-xs text-gray-600">
                    {marcaLoading
                      ? "Cargando…"
                      : (marcaInput ?? "").trim()
                        ? "Resultados"
                        : "Explorando"}
                  </div>

                  <div
                    className="max-h-72 overflow-auto"
                    onScroll={(e) => {
                      const el = e.currentTarget;

                      if (!marcaHasMore || marcaLoading) return;

                      if (
                        el.scrollTop + el.clientHeight >=
                        el.scrollHeight - 10
                      ) {
                        const q = (marcaInput ?? "").trim();

                        if (q) {
                          fetchMarcasSearch({
                            q,
                            page: (marcaPage ?? 0) + 1,
                          });
                        } else {
                          fetchMarcasBrowse({
                            page: (marcaPage ?? 0) + 1,
                          });
                        }
                      }
                    }}
                  >
                    {marcaItems.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-gray-50"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectMarca(m)}
                      >
                        <div className="text-sm text-gray-900">{m.nombre}</div>
                      </button>
                    ))}

                    {!marcaLoading && marcaItems.length === 0 && (
                      <div className="px-3 py-3 text-sm text-gray-500">
                        Sin resultados
                      </div>
                    )}

                    {marcaLoading && (
                      <div className="px-3 py-3 text-sm text-gray-500">
                        Cargando…
                      </div>
                    )}
                  </div>

                  {marcaHasMore && !marcaLoading && (
                    <div className="p-2 border-t text-center">
                      <button
                        type="button"
                        className="text-green-700 text-sm hover:underline"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          const q = (marcaInput ?? "").trim();

                          if (q) {
                            fetchMarcasSearch({
                              q,
                              page: (marcaPage ?? 0) + 1,
                            });
                          } else {
                            fetchMarcasBrowse({
                              page: (marcaPage ?? 0) + 1,
                            });
                          }
                        }}
                      >
                        Cargar más
                      </button>
                    </div>
                  )}
                </div>
              )}

              {errors.marca && (
                <p className="mt-1 text-xs text-red-600">{errors.marca}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] items-start gap-3">
            <label className="text-gray-700 mt-2">Descripción/Modelo:</label>

            <div data-field="descripcion">
              <textarea
                rows={3}
                className={`border rounded-md px-3 py-2 bg-white w-full ${
                  errors.descripcion ? "border-red-500" : "border-gray-300"
                }`}
                style={{ textTransform: "uppercase" }}
                value={descripcion}
                onFocus={touchRequired}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  setDescripcion(value);
                  setErrors((p) => ({ ...p, descripcion: undefined }));
                }}
              />

              {errors.descripcion && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.descripcion}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 3) Complete el Límite */}
      <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2">
        3. Complete el Límite
      </h3>

      <div
        className={`rounded-lg border border-gray-200 bg-gray-50 p-4 md:p-5 mb-4 ${
          overlay.open ? "opacity-70 pointer-events-none select-none" : ""
        }`}
      >
        <div className="space-y-3 text-sm md:text-base">
          <div
            className="grid grid-cols-1 md:grid-cols-[160px_240px] items-center gap-3"
            data-field="limite"
          >
            <label className="text-gray-700">Límite:</label>

            <div>
              <input
                type="text"
                inputMode="numeric"
                className={`border rounded-md px-3 py-2 bg-white w-full ${
                  errors.limite ? "border-red-500" : "border-gray-300"
                }`}
                value={limite}
                onFocus={touchRequired}
                onChange={(e) => {
                  const raw = e.target.value.replace(/\./g, "");

                  if (raw === "" || raw === null) {
                    setLimite("");
                    setExcedioSaldo(false);
                    setErrors((p) => ({ ...p, limite: undefined }));
                    return;
                  }

                  let val = Number(raw);

                  if (Number.isNaN(val)) {
                    setLimite("");
                    setExcedioSaldo(false);
                    setErrors((p) => ({
                      ...p,
                      limite: "Ingrese un número válido.",
                    }));
                    return;
                  }

                  if (val <= 0) {
                    val = 1;
                  }

                  if (saldoMax != null && val > saldoMax) {
                    val = saldoMax;
                    setExcedioSaldo(true);
                  } else {
                    setExcedioSaldo(false);
                  }

                  setLimite(formatMiles(val));
                  setErrors((p) => ({ ...p, limite: undefined }));
                }}
              />

              {errors.limite && (
                <p className="mt-1 text-xs text-red-600">{errors.limite}</p>
              )}
            </div>
          </div>

          {saldoMax != null && (
            <p
              className={`text-xs md:text-sm ${
                excedioSaldo ? "text-red-600" : "text-gray-600"
              }`}
            >
              Recuerde: el límite debe ser menor o igual a{" "}
              <b>{fmtMoney(saldoMax)}</b>.
            </p>
          )}
        </div>
      </div>

      {/* 4) Centro de Costo y Observaciones */}
      <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2">
        4. Centro de Costo y Observaciones
      </h3>

      <div
        className={`rounded-lg border border-gray-200 bg-gray-50 p-4 md:p-5 mb-4 ${
          overlay.open ? "opacity-70 pointer-events-none select-none" : ""
        }`}
      >
        <div className="space-y-4 text-sm md:text-base">
          <div className="grid grid-cols-1 md:grid-cols-[160px_320px] items-center gap-3">
            <label className="text-gray-700">Centro Costo:</label>

            <input
              className="border border-gray-300 rounded-md px-3 py-2 bg-white"
              style={{ textTransform: "uppercase" }}
              value={centroCosto}
              onFocus={touchRequired}
              onChange={(e) => setCentroCosto(e.target.value.toUpperCase())}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] items-start gap-3">
            <label className="text-gray-700 mt-2">Obs:</label>

            <textarea
              rows={3}
              className="border border-gray-300 rounded-md px-3 py-2 bg-white"
              style={{ textTransform: "uppercase" }}
              value={obs}
              onFocus={touchRequired}
              onChange={(e) => setObs(e.target.value.toUpperCase())}
            />
          </div>
        </div>
      </div>

      {/* 5) Capacidad del tanque */}
      <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2">
        5. Capacidad del tanque
      </h3>

      <div
        className={`rounded-lg border border-gray-200 bg-gray-50 p-4 md:p-5 mb-4 ${
          overlay.open ? "opacity-70 pointer-events-none select-none" : ""
        }`}
      >
        <div
          className="grid grid-cols-1 md:grid-cols-[220px_240px] items-center gap-3 text-sm md:text-base"
          data-field="capacidadTanque"
        >
          <label className="text-gray-700">Capacidad del Tanque:</label>

          <div>
            <InputNumberNoScroll
              className={`border rounded-md px-3 py-2 bg-white w-full ${
                errors.capacidadTanque ? "border-red-500" : "border-gray-300"
              }`}
              value={capacidadTanque}
              onFocus={touchRequired}
              onChange={(e) => {
                const v = e.target.value;

                setCapacidadTanque(v);

                const n = Number(v);

                if (v !== "" && n < 0) {
                  setErrors((p) => ({
                    ...p,
                    capacidadTanque:
                      "La capacidad del tanque no puede ser negativa.",
                  }));
                } else {
                  setErrors((p) => ({
                    ...p,
                    capacidadTanque: undefined,
                  }));
                }
              }}
            />

            {errors.capacidadTanque && (
              <p className="mt-1 text-xs text-red-600">
                {errors.capacidadTanque}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* 6) Productos asociados */}
      <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2">
        6. Productos asociados
      </h3>

      <div
        className={`rounded-lg border border-gray-200 bg-gray-50 p-4 md:p-5 mb-4 ${
          overlay.open ? "opacity-70 pointer-events-none select-none" : ""
        }`}
        data-field="productos"
      >
        <label className="text-gray-700">Producto:</label>

        <div className="text-sm md:text-base relative w-full md:w-[520px]">
          <ProductoMultiSelect
            value={prodSelectedIds}
            onChange={(ids) => {
              setProdSelectedIds(ids);
              setErrors((prev) => ({
                ...prev,
                productos: undefined,
              }));
            }}
            disabled={overlay.open}
          />
        </div>

        {errors.productos && (
          <p className="mt-2 text-xs text-red-600">{errors.productos}</p>
        )}
      </div>

      {/* 7) Estaciones de Servicio Asociadas */}
      <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2">
        7. Estaciones de Servicio Asociadas
      </h3>

      <div
        className={`rounded-lg border border-gray-200 bg-gray-50 p-4 md:p-5 mb-6 ${
          overlay.open ? "opacity-70 pointer-events-none select-none" : ""
        }`}
        data-field="eess"
      >
        <label className="text-gray-700">EE.SS:</label>

        <div className="text-sm md:text-base relative w-full md:w-[520px]">
          <EessMultiSelect
            value={estSelectedIds}
            onChange={(ids) => {
              setEstSelectedIds(ids);
              setErrors((prev) => ({
                ...prev,
                eess: undefined,
              }));
            }}
            disabled={overlay.open}
          />
        </div>

        {errors.eess && (
          <p className="mt-2 text-xs text-red-600">{errors.eess}</p>
        )}
      </div>

      {/* Footer */}
      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          type="button"
          className="btn-outline px-4 py-2"
          onClick={handleCancelar}
        >
          <FaArrowLeft className="mr-2 inline" />
          Cancelar
        </button>

        <button
          type="button"
          onClick={handleGuardar}
          disabled={!isGuardarEnabled}
          className={
            isGuardarEnabled
              ? "btn-fil px-4 py-2"
              : "btn-fil-disabled px-4 py-2"
          }
        >
          <FaSave className="mr-2 inline" />

          {overlay.open && overlay.mode === "loading"
            ? "Guardando..."
            : saving
              ? "Guardando..."
              : "Guardar"}
        </button>
      </div>

      {/* Overlay */}
      {overlay.open && (
        <div className="fixed inset-0 z-[9999] bg-black/25 backdrop-blur-[1px] flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-xl px-6 py-5 w-[90%] max-w-md text-center">
            {overlay.mode === "loading" && (
              <>
                <div className="mx-auto mb-3 h-10 w-10 rounded-full border-4 border-gray-300 border-t-transparent animate-spin" />

                <div className="text-gray-800 font-semibold">
                  Generando solicitud…
                </div>

                <div className="mt-1 text-sm text-gray-600">
                  Por favor, espere sin cerrar ni recargar la página.
                </div>
              </>
            )}

            {overlay.mode === "success" && (
              <>
                <div className="mx-auto mb-3 h-10 w-10 rounded-full border-4 border-green-400 flex items-center justify-center">
                  <span className="text-green-600 text-lg">✓</span>
                </div>

                <div className="text-green-700 font-semibold">
                  {overlay.msg}
                </div>

                <div className="mt-4 flex items-center justify-center gap-2">
                  {overlay.numero && (
                    <button
                      type="button"
                      className="btn px-4 py-2"
                      onClick={abrirPermisosConTarjetaCreada}
                    >
                      Configurar tarjeta
                    </button>
                  )}
                </div>
              </>
            )}

            {overlay.mode === "error" && (
              <>
                <div className="mx-auto mb-3 h-10 w-10 rounded-full border-4 border-red-400 flex items-center justify-center">
                  <span className="text-red-600 text-lg">!</span>
                </div>

                <div className="text-red-700 font-semibold">{overlay.msg}</div>

                <div className="mt-4">
                  <button
                    type="button"
                    className="btn px-4 py-2"
                    onClick={() =>
                      setOverlay({
                        open: false,
                        mode: "hidden",
                        msg: "",
                        numero: null,
                      })
                    }
                  >
                    Entendido
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal PermisoTarjeta */}
      {mostrarPermisos && (
        <ModalPortal>
          <div className="fixed inset-0 z-[10000] bg-black/40">
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <div className="bg-white w-full max-w-[1200px] h-[90vh] rounded-2xl shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <h3 className="text-base font-semibold text-gray-800">
                    Configuración permisos de tarjetas
                  </h3>
                </div>

                <div className="h-[calc(90vh-56px)] overflow-auto">
                  <PermisoTarjeta
                    key={tarjetaSel?.numero || tarjetaSel?.id}
                    tarjetaId={tarjetaSel?.id}
                    numeroTarjeta={tarjetaSel?.numero}
                    clienteNombre={tarjetaSel?.clienteNombre}
                    chapa={tarjetaSel?.chapa}
                    vehiculoMarca={tarjetaSel?.vehiculoMarca}
                    vehiculoEtiqueta={tarjetaSel?.vehiculoEtiqueta}
                    clienteId={tarjetaSel?.clienteId}
                    codigoTipo={tarjetaSel?.codigoTipo}
                    valorLimite={tarjetaSel?.valorLimite}
                    onVolver={() => {
                      setMostrarPermisos(false);
                      onClose();
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
}

export default TarjetaCliente;